import type { GoalsModel } from '@quiz-configs/models/quiz.model';

export interface FunnelStateModel {
  ageId: string
  goals: GoalsModel | null
  addGoal: boolean
  startLevel: number
}

export interface DecodedFunnelState {
  state: Partial<FunnelStateModel>
  nonce: number
}

export const FUNNEL_STATE_QUERY_KEY = 's';

export const LEGACY_STATE_QUERY_KEYS = [
  'main_goal',
  'secondary_goal',
  'age',
  'addGoal',
];

export const GOAL_PAIRS: readonly GoalsModel[] = [
  { main: 'goal_erectile_function', additional: 'add_goal_ejaculation_control' },
  { main: 'goal_ejaculation_control', additional: 'add_goal_erectile_function' },
  { main: 'goal_sexual_wellness', additional: '' },
];

const CODE_VERSION = '1';
const AGE_VARIANTS = 5;
const GOAL_VARIANTS = GOAL_PAIRS.length + 1;
const ADD_GOAL_VARIANTS = 2;
const LEVEL_VARIANTS = 5;
const STATE_VARIANTS = AGE_VARIANTS * GOAL_VARIANTS * ADD_GOAL_VARIANTS * LEVEL_VARIANTS;

const NONCE_VARIANTS = 4096;
const RAW_LIMIT = STATE_VARIANTS * NONCE_VARIANTS;

const HALF_BITS = 10;
const HALF_MASK = (1 << HALF_BITS) - 1;
const ROUND_KEYS = [0x2F1B, 0x7A93, 0x51C7, 0x3E29];

const PAYLOAD_LENGTH = 4;
const CHECKSUM_LENGTH = 2;
const CHECKSUM_VARIANTS = 36 ** CHECKSUM_LENGTH;
const CHECKSUM_FACTOR = 40503;
const CODE_LENGTH = CODE_VERSION.length + PAYLOAD_LENGTH + CHECKSUM_LENGTH;

function roundFunction(half: number, key: number): number {
  const mixed = ((half ^ key) * 0x9E37) & 0x7FFFFFFF;

  return (mixed ^ (mixed >>> 3) ^ (mixed >>> 7)) & HALF_MASK;
}

function feistel(value: number, keys: readonly number[]): number {
  let left = (value >>> HALF_BITS) & HALF_MASK;
  let right = value & HALF_MASK;

  for (const key of keys) {
    const next = left ^ roundFunction(right, key);
    left = right;
    right = next;
  }

  return ((left << HALF_BITS) | right) >>> 0;
}

function scramble(value: number): number {
  return feistel(value, ROUND_KEYS);
}

function unscramble(value: number): number {
  let left = (value >>> HALF_BITS) & HALF_MASK;
  let right = value & HALF_MASK;

  for (let index = ROUND_KEYS.length - 1; index >= 0; index -= 1) {
    const previous = right ^ roundFunction(left, ROUND_KEYS[index]);
    right = left;
    left = previous;
  }

  return ((left << HALF_BITS) | right) >>> 0;
}

function checksumOf(raw: number): string {
  return ((raw * CHECKSUM_FACTOR) % CHECKSUM_VARIANTS)
    .toString(36)
    .padStart(CHECKSUM_LENGTH, '0');
}

function clampIndex(value: number | null | undefined, variants: number): number {
  if (value == null || !Number.isFinite(value) || value <= 0 || value >= variants) {
    return 0;
  }

  return Math.trunc(value);
}

function normalizeNonce(nonce: number): number {
  if (!Number.isFinite(nonce) || nonce < 0 || nonce >= NONCE_VARIANTS) {
    return 0;
  }

  return Math.trunc(nonce);
}

export function getGoalsIndex(goals: GoalsModel | null | undefined): number {
  if (!goals?.main) {
    return 0;
  }

  return GOAL_PAIRS.findIndex(pair => pair.main === goals.main) + 1;
}

export function getGoalsByMain(main: string | null | undefined): GoalsModel | null {
  return GOAL_PAIRS.find(pair => pair.main === main) ?? null;
}

export function createFunnelStateNonce(): number {
  const buffer = new Uint16Array(1);

  globalThis.crypto.getRandomValues(buffer);

  return buffer[0] % NONCE_VARIANTS;
}

export function encodeFunnelState(state: Partial<FunnelStateModel>, nonce: number): string {
  const age = clampIndex(Number(state.ageId), AGE_VARIANTS);
  const goal = clampIndex(getGoalsIndex(state.goals), GOAL_VARIANTS);
  const addGoal = state.addGoal ? 1 : 0;
  const level = clampIndex(state.startLevel, LEVEL_VARIANTS);

  const stateIndex = (((age * GOAL_VARIANTS + goal) * ADD_GOAL_VARIANTS) + addGoal) * LEVEL_VARIANTS + level;
  const raw = normalizeNonce(nonce) * STATE_VARIANTS + stateIndex;
  const payload = scramble(raw).toString(36).padStart(PAYLOAD_LENGTH, '0');

  return `${CODE_VERSION}${payload}${checksumOf(raw)}`;
}

export function decodeFunnelState(code: unknown): DecodedFunnelState | null {
  if (typeof code !== 'string' || code.length !== CODE_LENGTH || !code.startsWith(CODE_VERSION)) {
    return null;
  }

  const payload = Number.parseInt(code.slice(CODE_VERSION.length, CODE_VERSION.length + PAYLOAD_LENGTH), 36);

  if (Number.isNaN(payload)) {
    return null;
  }

  const raw = unscramble(payload);

  if (raw >= RAW_LIMIT || checksumOf(raw) !== code.slice(-CHECKSUM_LENGTH)) {
    return null;
  }

  const stateIndex = raw % STATE_VARIANTS;
  const level = stateIndex % LEVEL_VARIANTS;
  const addGoal = Math.floor(stateIndex / LEVEL_VARIANTS) % ADD_GOAL_VARIANTS;
  const goal = Math.floor(stateIndex / (LEVEL_VARIANTS * ADD_GOAL_VARIANTS)) % GOAL_VARIANTS;
  const age = Math.floor(stateIndex / (LEVEL_VARIANTS * ADD_GOAL_VARIANTS * GOAL_VARIANTS));

  const state: Partial<FunnelStateModel> = {};
  const goals = GOAL_PAIRS[goal - 1];

  if (age) {
    state.ageId = String(age);
  }

  if (goals) {
    state.goals = { ...goals };
  }

  if (addGoal) {
    state.addGoal = true;
  }

  if (level) {
    state.startLevel = level;
  }

  return {
    state,
    nonce: Math.floor(raw / STATE_VARIANTS),
  };
}
